import React, { useEffect, useRef, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import type { TNMIAddress, TNMIServicesRequired } from './types';
import { NMIResolutionContext, useNMIResolutionContext } from './useNMIResolutionContext';
import { BelowThresholdAlert } from './BelowThresholdAlert';
import { ConfirmAddressSuccess } from './ConfirmAddressSuccess';
import { HardStopAlert } from './HardStopAlert';
import { ConfirmAddressDialog, IAddressConfirmation } from './ConfirmAddressDialog';

import { useNMIResolution } from './useNMIResolution';
import { useAddressesChanged } from './useAddressesChanged';

import { alert, logger } from '@origin-digital/reporting-client';
import { Address, updateAddressSelected } from '@origin-digital/address-lookup';
import { CESErrorAlert, type CESErrorAlertPresentationProps } from './CESErrorAlert';
import { useDeepCompareEffect, CUSTOMER_TYPE, CustomerType } from '@origin-digital/sales-common';

export enum NMIResolutionState {
  Init,
  InProgress, // at the moment this is used to identify the transition state when trying to check if an address is CES address
  ExactMatchFound,
  NmiHardStop,
  NeedsToConfirmNmi,
  CES,
  Completed,
}

export interface NMIResolutionRenderCallbacks {
  resolutionState: NMIResolutionState;
  triggerConfirmAddressDialog: () => void;
  triggerConfirmAddressSuccessSnackbar: () => void;
}

interface NMIResolutionRootProps {
  addresses: TNMIAddress[];
  address?: Address | TNMIAddress;
  selectedFuel: TNMIServicesRequired;
  children?: React.ReactNode | ((callbacks: NMIResolutionRenderCallbacks) => React.ReactNode);
  isEspec?: boolean;
  onResolutionStateChange?: (state: NMIResolutionState) => void;
}

const NMIResolutionRoot: React.FC<NMIResolutionRootProps> = ({
  addresses,
  address,
  selectedFuel,
  children,
  isEspec,
  onResolutionStateChange,
}) => {
  // Use handleResolutionStateChange when changing state to ensure onResolutionStateChange is called
  const [resolutionState, setResolutionState] = useState<NMIResolutionState>(
    NMIResolutionState.Init
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showConfirmSuccess, setShowConfirmSuccess] = useState(false);
  const [cesSearchTerm, setCesSearchTerm] = useState('');
  const cesResolutionRequestId = useRef(0);

  const { isNMIBelowThreshold, isNMIHardStop, isExactMatch, isMirnNotFound, checkIfCesAddress } =
    useNMIResolution();

  const { hasAddressesChanged } = useAddressesChanged(addresses);

  useEffect(() => {
    return () => {
      cesResolutionRequestId.current += 1;
    };
  }, []);

  useDeepCompareEffect(() => {
    const requestId = ++cesResolutionRequestId.current;
    const isCurrentRequest = () => requestId === cesResolutionRequestId.current;

    if (isExactMatch(addresses)) {
      handleResolutionStateChange(NMIResolutionState.ExactMatchFound);
      return;
    }

    if (isNMIHardStop(addresses) && isMirnNotFound(addresses)) {
      if (isEmpty(address)) {
        handleResolutionStateChange(NMIResolutionState.InProgress);
        return;
      }
      handleResolutionStateChange(NMIResolutionState.InProgress);
      checkIfCesAddress(address)
        .then(({ isCes, searchTerm }) => {
          if (!isCurrentRequest()) return;

          if (isCes) setCesSearchTerm(searchTerm);
          handleResolutionStateChange(
            isCes ? NMIResolutionState.CES : NMIResolutionState.NmiHardStop
          );
        })
        .catch((err) => {
          if (!isCurrentRequest()) return;

          logger.error('Failed to check CES address:', err);
          handleResolutionStateChange(NMIResolutionState.NmiHardStop);
        });
      return;
    }

    if (isNMIHardStop(addresses)) {
      handleResolutionStateChange(NMIResolutionState.NmiHardStop);
      return;
    }

    if (isNMIBelowThreshold(addresses, selectedFuel)) {
      hasAddressesChanged() && setShowConfirmDialog(true);
      handleResolutionStateChange(NMIResolutionState.NeedsToConfirmNmi);
      return;
    }

    handleResolutionStateChange(NMIResolutionState.Init);
  }, [address, addresses, selectedFuel]);

  const renderCallbacks: NMIResolutionRenderCallbacks = {
    resolutionState,
    triggerConfirmAddressDialog: () => setShowConfirmDialog(true),
    triggerConfirmAddressSuccessSnackbar: () => setShowConfirmSuccess(true),
  };

  const handleResolutionStateChange = (state: NMIResolutionState) => {
    setResolutionState(state);
    onResolutionStateChange?.(state);
  };

  return (
    <NMIResolutionContext.Provider
      value={{
        addresses: addresses ?? [],
        resolutionState,
        handleResolutionStateChange,
        showConfirmSuccess,
        setShowConfirmSuccess,
        selectedFuel,
        showConfirmDialog,
        setShowConfirmDialog,
        isEspec,
        searchTerm: cesSearchTerm,
      }}
    >
      {typeof children === 'function' ? children(renderCallbacks) : children}
    </NMIResolutionContext.Provider>
  );
};

const HardStop = ({ triggerFindByNmi }: { triggerFindByNmi: () => void }) => {
  const { resolutionState, selectedFuel } = useNMIResolutionContext('HardStopAlert');

  return resolutionState === NMIResolutionState.NmiHardStop ? (
    <HardStopAlert hasGasSelected={selectedFuel.gas} triggerFindByNmi={triggerFindByNmi} />
  ) : null;
};

const BelowThreshold = () => {
  const { resolutionState, addresses, setShowConfirmDialog } =
    useNMIResolutionContext('BelowThresholdAlert');

  useEffect(() => {
    if (resolutionState === NMIResolutionState.NeedsToConfirmNmi) {
      alert.base('info', {
        alertMessage: 'Please confirm your address',
        alertCode: 'confirm-address',
      });
    }
  }, [resolutionState]);

  return resolutionState === NMIResolutionState.NeedsToConfirmNmi ? (
    <BelowThresholdAlert
      onClick={() => setShowConfirmDialog(true)}
      label={addresses.length === 1 ? 'Review Address' : 'Review Addresses'}
    />
  ) : null;
};

const ConfirmAddress = ({
  onConfirm,
  onClose,
  shouldShowChat,
  onEnterAddressManually,
  onEnterNmiManually,
  open,
}: {
  onConfirm: (address: TNMIAddress) => void;
  open?: boolean;
  onClose?: () => void;
} & Pick<
  IAddressConfirmation,
  'shouldShowChat' | 'onEnterAddressManually' | 'onEnterNmiManually'
>) => {
  const [submitting, setSubmitting] = useState(false);
  const {
    resolutionState,
    handleResolutionStateChange,
    addresses,
    setShowConfirmSuccess,
    showConfirmDialog,
    setShowConfirmDialog,
  } = useNMIResolutionContext('ConfirmAddressDialog');

  const isControlled = open !== undefined && !showConfirmDialog;
  const isOpen = isControlled ? open : showConfirmDialog;

  const handleClose = () => {
    if (isControlled) {
      onClose?.();
    } else {
      setShowConfirmDialog(false);
    }
  };

  return resolutionState === NMIResolutionState.NeedsToConfirmNmi ? (
    <ConfirmAddressDialog
      addresses={addresses}
      open={isOpen}
      onClose={handleClose}
      submitting={submitting}
      onEnterAddressManually={
        onEnterAddressManually
          ? () => {
              onEnterAddressManually();
              setShowConfirmDialog(false);
            }
          : undefined
      }
      onEnterNmiManually={
        onEnterNmiManually
          ? () => {
              onEnterNmiManually();
              setShowConfirmDialog(false);
            }
          : undefined
      }
      onConfirm={async (nmi) => {
        const item = addresses.find((addr) => addr.nmi === nmi);
        try {
          if (item && item.originAddressId) {
            setSubmitting(true);
            await updateAddressSelected('/api/sapaddress/v1/addresses', item.originAddressId);
            onConfirm?.({ ...item, isSelected: true });
            handleResolutionStateChange(NMIResolutionState.Completed);
            setShowConfirmSuccess(true);
          }
        } catch (e) {
          handleResolutionStateChange(NMIResolutionState.NeedsToConfirmNmi);
        } finally {
          setSubmitting(false);
          handleClose();
        }
      }}
      shouldShowChat={shouldShowChat}
    />
  ) : null;
};

const ConfirmSuccess = () => {
  const { showConfirmSuccess, setShowConfirmSuccess } =
    useNMIResolutionContext('ConfirmAddressSuccess');
  return (
    <ConfirmAddressSuccess open={showConfirmSuccess} onClose={() => setShowConfirmSuccess(false)} />
  );
};

const CESError = ({
  customerType = CUSTOMER_TYPE.RESIDENTIAL,
  ...presentationProps
}: CESErrorAlertPresentationProps & {
  customerType?: CustomerType;
  cesBaseUrl?: string;
}) => {
  const { resolutionState, isEspec, searchTerm } = useNMIResolutionContext('CESErrorAlert');

  if (resolutionState !== NMIResolutionState.CES) return null;

  return (
    <CESErrorAlert
      {...presentationProps}
      customerType={customerType}
      isEspec={isEspec}
      searchTerm={searchTerm}
    />
  );
};

export const NMIResolution = Object.assign(NMIResolutionRoot, {
  HardStopAlert: HardStop,
  ConfirmAddressDialog: ConfirmAddress,
  BelowThresholdAlert: BelowThreshold,
  ConfirmAddressSuccess: ConfirmSuccess,
  CESErrorAlert: CESError,
});
